<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type { RequiredKeys } from '@/utils/types';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed } from 'vue';
import { formatInTimeZone } from 'date-fns-tz';

import DetailedJobInfoDescListItem from '@/components/items/DetailedJobInfoDescListItem.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import * as commonConstants from '@/constants/common';

export interface Props {
  job?: ReadonlyDeep<
    RequiredKeys<
      Partial<
        Pick<
          openapi.JobDespatch,
          | 'booked_by'
          | 'booked_at'
          | 'cancelled_at'
          | 'caller'
          | 'is_pod_required'
          | 'phone'
          | 'ref1'
          | 'ref2'
          | 'ref3'
          | 'ref4'
          | 'invoice_status'
        >
      >
    >
  >;
  loading?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  job: undefined,
  loading: false,
});

const job = computed<Partial<NonNullable<Props['job']>>>(() => props.job || {});

const timeZone = 'Australia/Sydney';

function formatDate(date: string, timeZone: string): string {
  return formatInTimeZone(date, timeZone, 'hh:mm a, MMM d'); // 04:50 PM, Sep 24
}
</script>

<!-- TODO similar to @/components/descLists/DetailedJobAdditionalInfoDescList.vue -->
<template>
  <dl
    :class="$style.dispatchJobAdditionalInfoDescList"
    data-testid="dispatchJobAdditionalInfoDescList"
  >
    <div :class="$style.wrapper">
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Phone">
          <template v-if="job.phone && job.phone.length > 1">
            <a :href="`callto://${job.phone}`" :class="$style.a">
              <KParentTooltip>Call via Skype</KParentTooltip>
              <v-icon :class="$style.icon" icon="mdi-phone-outline"></v-icon>
              {{ job.phone }}
            </a>
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Caller">
          <template v-if="job.caller">
            {{ job.caller }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Booked by">
          <template v-if="job.booked_by">
            {{ job.booked_by }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Booked at">
          <template v-if="job.booked_at">
            <KParentTooltip>{{ job.booked_at }}</KParentTooltip>
            {{ formatDate(job.booked_at, timeZone) }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Cancelled at">
          <template v-if="job.cancelled_at">
            <KParentTooltip>{{ job.cancelled_at }}</KParentTooltip>
            {{ formatDate(job.cancelled_at, timeZone) }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Reference 1">
          <template v-if="job.ref1">
            {{ job.ref1 }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Reference 2">
          <template v-if="job.ref2">
            {{ job.ref2 }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Reference 3">
          <template v-if="job.ref3">
            {{ job.ref3 }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Reference 4">
          <template v-if="job.ref4">
            {{ job.ref4 }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="POD required">
          <template v-if="props.loading">
            {{ commonConstants.EM_DASH }}
          </template>
          <template v-else-if="job.is_pod_required === true">Yes</template>
          <template v-else-if="job.is_pod_required === false">No</template>
          <template v-else>
            {{ commonConstants.EM_DASH }}
          </template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Invoice status">
          <template v-if="job.invoice_status">
            {{ job.invoice_status }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
    </div>
  </dl>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.dispatchJobAdditionalInfoDescList {
  line-height: 1.4;
  font-size: 16px;
  margin: 0;
}
  .wrapper {
    margin-bottom: -16px;
    columns: 3 150px;
    column-gap: 20px;
  }
    .itemWrapper {
    }
      .item {
        display: inline-block;
        margin: 0 0 16px 0;
      }
        .a {
          text-decoration: none;
        }
          .icon {
            font-size: 17px;
            margin-top: -1px;
          }
</style>
